import { Controller } from "@hotwired/stimulus";
import { inputSubmitWith } from "lib/utilities";
import { useDocumentListener, useElementListener } from "lib/event_helpers";
import { onTurboStreamConnected } from 'lib/turbo_stream_utils'

export default class extends Controller {
  static values = { preloadQuery: String };
  static targets = ["messages", "assistantTemplate", "thinkingMessage", "scrollIndicator"]

  continuousAnimationFrameRequest = null;
  scrollAnimationFrameRequest = null;
  smoothScrollTarget = null;
  shouldAutoScroll = true;
  lastScrollTop = 0;

  connect() {
    this.relatedQuestionsListeners();
    this.messageListeners();
    this.listenForErrors();
    this.preloadQuery();
    this.dispatchInitialState();
    this.scrollToBottom();

    useElementListener(this, this.scrollIndicatorTarget, 'click', this.queueAutoScroll)
    useDocumentListener(this, 'chat--fullscreen:cancel', this.queueAutoScroll);
  }

  listenForErrors() {
    useDocumentListener(this, 'turbo:frame-missing', (event) => {
      event.preventDefault()
      this.insertFlashMessage('Something went wrong. Please try again in a moment.');
    });
  }

  insertFlashMessage(message) {
    var flashDiv = document.getElementById('flash');

    // Create the new alert div
    var alertDiv = document.createElement('div');
    alertDiv.className = 'alert alert-primary alert-dismissible fade show';
    alertDiv.textContent = message;

    // Create the close button
    var closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'btn-close';
    closeButton.dataset.bsDismiss = 'alert';
    closeButton.setAttribute('aria-label', 'Close');

    // Append the close button to the alert div
    alertDiv.appendChild(closeButton);

    // Append the alert div to the flash div
    flashDiv.appendChild(alertDiv);
  }

  relatedQuestionsListeners() {
    useDocumentListener(this, 'chat--related-questions:show', this.startContinuousAutoscroll);
    useDocumentListener(this, 'chat--related-questions:complete', this.cancelContinuousAutoscroll);
  }

  messageListeners() {
    this.onQuerySubmit();
    this.onNewMessage();
    this.onMessageUpdate();
    this.onErrorMessage();

    useDocumentListener(this, 'chat--message:collapse-show', this.startContinuousAutoscroll);
    useDocumentListener(this, 'chat--message:collapse-shown', this.cancelContinuousAutoscroll);
  }

  onQuerySubmit() {
    useDocumentListener(this, "chat--form:requested", (event) => {
      this.updateUrlForShow(event);
    });
  }

  onNewMessage() {
    useDocumentListener(this, "chat--message:new", (event) => {
      this.shouldAutoScroll = true; // Force auto scroll when a new message appears
      if (event.target.dataset.role === 'user') {
        this.showThinkingIndicator();
        this.queueStillWaitingMessage();
        this.scrollToBottom();
      } else if (event.target.dataset.role === 'assistant') {
        this.nullifyStillWaitingTimeout()
        this.hideThinkingIndicator();
        this.scrollToTopOfAssistantMessage(event);
      }
    });
  }

  onMessageUpdate() {
    // Invoked whenever the newest message is re-rendered from
    //its turbo stream
    useDocumentListener(this, "chat--message:update", (event) => {
      this.scrollToTopOfAssistantMessage(event);
    });
  }

  onErrorMessage() {
    useDocumentListener(this, "chat--error:new", (event) => {
      this.nullifyStillWaitingTimeout();
      this.hideThinkingIndicator();
    });
  }

  showThinkingIndicator() {
    this.messagesTarget.appendChild(this.thinkingMessageTarget);
    this.thinkingMessageTarget.style.display = 'block';
  }

  queueStillWaitingMessage() {
    this.timeoutId = setTimeout(this.addStillWaitingMessage.bind(this), 4000);
  }

  queueAutoScroll() {
    // Use a timeout to ensure the layout has re-rendered
    setTimeout(() => {
      this.shouldAutoScroll = true;
      this.scrollToBottom();
    }, 0);
  }

  startContinuousAutoscroll(event) {
    if (this.continuousAnimationFrameRequest == null) {
      const callback = () => {
        this.updateScrollIndicatorStatus();
        this.scrollToElement(event.detail?.message);
        this.continuousAnimationFrameRequest = requestAnimationFrame(callback);
      };
      this.continuousAnimationFrameRequest = requestAnimationFrame(callback);
    }
  }

  cancelContinuousAutoscroll(event) {
    if (this.continuousAnimationFrameRequest != null) {
      cancelAnimationFrame(this.continuousAnimationFrameRequest)
      this.continuousAnimationFrameRequest = null;
    }
    this.scrollToElement(event.detail?.message);
    this.checkScrollHeight();
  }

  checkScrollHeight() {
    if (this.messagesTarget.scrollHeight === this.messagesTarget.clientHeight) {
      // If our scroll bar dissapears (like if related questions are collapsed), we
      // should re-enable autoscroll
      this.shouldAutoScroll =  true
    }
  }

  // Scroll to the bottom of the chat window
  scrollToBottom() {
    this.scrollToElement();
  }

  // This could be from server side but it doesn't simplify it much
  updateUrlForShow(event) {
    if (event.detail.uuid && !window.location.href.includes(event.detail.uuid)) {
      // Update the URL to include the UUID
      let newUrl = window.location.href.replace(/(?:chat\/new|conversations\/[a-f0-9-]+)/, "conversations/" + event.detail.uuid);
      history.pushState({}, null, newUrl);
    }
  }

  preloadQuery() {
    let queryValue = this.preloadQueryValue.trim();
    if (queryValue.length > 0) {
      onTurboStreamConnected("windowMessagesStream", () => {
        inputSubmitWith("user-input", this.preloadQueryValue);
      });
    }
  }

  dispatchInitialState() {
    let latestMessage = this.latestMessage();
    if (latestMessage) {
      this.dispatch("loaded", { target: latestMessage });
    }
  }

  // Last message in chat window which has an id value
  latestMessage() {
    const messages = Array.from(this.messagesTarget.querySelectorAll(".chat--message--wrapper"));
    const filteredMessages = messages.filter(message => {
      let idValue = message.getAttribute('data-chat--message-message-id-value');
      return idValue && idValue.trim() !== "";
    });
    return filteredMessages.length > 0 ? filteredMessages[filteredMessages.length - 1] : null;
  }

  nullifyStillWaitingTimeout() {
    // Remove still waiting timeout message if it exists
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    // Remove still waiting message if it exists
    const messageElement = document.getElementById("message-waiting");
    if (messageElement) {
      messageElement.remove();
    }
  }

  hideThinkingIndicator() {
    // Hide thinking indicator
    this.thinkingMessageTarget.style.display = 'none';
    // Move thinking indicator to the bottom
    // to be ready for the next message
    this.messagesTarget.appendChild(this.thinkingMessageTarget);
  }

  addStillWaitingMessage() {
    const waitingPhrases = [
      "Looking for the best answer to your question...",
      "Finding the best information for you...",
      "Compiling insights to share with you..."
    ];

    const randomIndex = Math.floor(Math.random() * waitingPhrases.length);
    const selectedPhrase = waitingPhrases[randomIndex];
    // font-family: 'Nimbus Mono PS', 'Courier New', monospace;
    this.append(this.assistantTemplateTarget, selectedPhrase, "message-waiting");
    // Move thinking indicator to the bottom
    this.messagesTarget.appendChild(this.thinkingMessageTarget);
    this.scrollToBottom(this.thinkingMessageTarget);
  }

  append(target, content, messageId = null) {
    const messageWrapper = target.querySelector('.chat--message--wrapper');
    const messageContent = messageWrapper.querySelector('.chat--message');
    const pElement = document.createElement('p');
    pElement.textContent = content;
    messageContent.innerHTML = '';
    messageContent.appendChild(pElement);

    const clonedMessageWrapper = messageWrapper.cloneNode(true);

    if (messageId) {
      clonedMessageWrapper.id = messageId;
    }

    this.messagesTarget.appendChild(clonedMessageWrapper);
  }

  scrollToTopOfAssistantMessage(event) {
    this.scrollToElement(event.target);
  }

  scrollToElement(targetElement) {
    this.smoothScrollToElement(this.messagesTarget, targetElement);
  }

  smoothScrollToElement(chatWindow, targetElement) {
    if (!this.shouldAutoScroll) {
      return;
    }

    // Calculate the target scroll position based on the position of the target element
    // If targetElement is not provided, scroll to the bottom of the chatWindow
    const targetScrollTop = targetElement
      ? targetElement.offsetTop - chatWindow.offsetTop
      : chatWindow.scrollHeight - chatWindow.clientHeight;

    // Don't mess with our current scroll animation if it's a
    // repetitive request
    if (this.smoothScrollTarget == targetScrollTop) {
      return;
    }

    // Clear out any existing automated scrolling
    this.cancelAnimationFrameRequest();

    // Calculate the distance we'll be scrolling
    const distance = Math.abs(chatWindow.scrollTop - targetScrollTop);
    if (distance <= 0) {
      return;
    }

    // Keep track of where we're trying to get to
    this.smoothScrollTarget = targetScrollTop;

    // Scale our duration to the distance
    const duration = Math.min(1000, Math.round(distance / 5));

    // Don't animate if our animation is going to be too quick —
    // 20 miliseconds is around the threshold for 60fps rendering
    if (duration >= 20) {
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsedTime = currentTime - startTime;
        const progress = Math.min(elapsedTime / duration, 1); // Progress goes from 0 to 1
        const currentScrollTop = chatWindow.scrollTop;
        const scrollDistance = targetScrollTop - currentScrollTop;
        const newScrollTop = currentScrollTop + scrollDistance * progress;

        chatWindow.scrollTop = newScrollTop;

        // If the animation is not complete, request the next frame
        if (progress < 1) {
          this.animationFrameRequest = window.requestAnimationFrame(animateScroll);
        } else {
          this.animationFrameRequest = null;
          this.smoothScrollTarget = null; // Reset this so it doesn't interfere with future requests
          this.updateScrollIndicatorStatus(); // Check if we need to show the scroll indicator
        }
      };

      // Start the animation
      this.animationFrameRequest = window.requestAnimationFrame(animateScroll);
    } else {
      chatWindow.scrollTop = targetScrollTop;

      this.smoothScrollTarget = null; // Reset this so it doesn't interefere with future requests
      this.updateScrollIndicatorStatus(); // Check if we need to show the scroll indicator
    }
  }

  cancelAnimationFrameRequest() {
    if (this.scrollAnimationFrameRequest != null) {
      window.cancelAnimationFrame(this.scrollAnimationFrameRequest);
    }

    this.animationFrameRequest = null;
    this.smoothScrollTarget = null; // Reset this so it doesn't interfere with future scroll requests
  }

  handleScroll() {
    const scrollTop = this.messagesTarget.scrollTop;
    const maxScrollTop = this.messagesTarget.scrollHeight - this.messagesTarget.clientHeight;

    // On mobile, we sometimes have partial pixels, so add a slight buffer
    // to what we consider the "bottom"
    if (scrollTop >= maxScrollTop - 1) {
      // If we're at the bottom of the window, we should auto scroll
      this.shouldAutoScroll = true;
    } else {
      if (scrollTop < this.lastScrollTop || this.smoothScrollTarget && scrollTop > this.smoothScrollTarget) {
        // If we scroll up, cancel any other scrolling and disable auto scroll
        this.cancelAnimationFrameRequest();
        this.shouldAutoScroll = false;
      }
    }

    this.updateScrollIndicatorStatus();
    this.lastScrollTop = scrollTop;
  }

  updateScrollIndicatorStatus() {
    const scrollTop = this.messagesTarget.scrollTop;
    const maxScrollTop = this.messagesTarget.scrollHeight - this.messagesTarget.clientHeight;

    // On mobile, we sometimes have partial pixels, so let's add a bit of a buffer
    // for when the scroll arrow should appear
    if (scrollTop < maxScrollTop - 10)
      this.scrollIndicatorTarget.classList.remove('hidden')
    else
      this.scrollIndicatorTarget.classList.add('hidden')
  }

}